import { useState, useEffect, useCallback } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import {
  Bell, AlertTriangle, TrendingUp, Search, Eye, ShieldAlert,
  RefreshCw, Check, Loader2, ChevronRight, Clock, X,
} from "lucide-react";
import { toast } from "sonner";

const SEVERITY_STYLES = {
  high: { bg: "bg-red-50", border: "border-red-200", icon: "text-red-500", badge: "bg-red-100 text-red-700" },
  medium: { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-500", badge: "bg-amber-100 text-amber-700" },
  low: { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-500", badge: "bg-blue-100 text-blue-700" },
};

const TYPE_ICONS = {
  price_report_spike: AlertTriangle,
  high_friction: Eye,
  supply_gap: Search,
  view_spike: TrendingUp,
  stale_verification: ShieldAlert,
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const AlertCard = ({ alert, onDismiss, onSnooze, onResolve, onNavigate }) => {
  const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.low;
  const Icon = TYPE_ICONS[alert.alert_type] || Bell;
  const refBid = alert.ref_id && !alert.ref_id.startsWith("search:") ? alert.ref_id : null;

  return (
    <div
      className={`${style.bg} ${style.border} border rounded-xl p-3 space-y-2`}
      data-testid={`alert-card-${alert.alert_id}`}
    >
      <div className="flex items-start gap-2.5">
        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${style.icon}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 leading-tight">{alert.title}</p>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{alert.detail}</p>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${style.badge}`}>
          {alert.severity}
        </span>
      </div>
      <div className="flex items-center justify-between pt-1">
        <span className="text-[10px] text-slate-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {timeAgo(alert.created_at)}
        </span>
        <div className="flex items-center gap-2">
          {refBid && (
            <button
              onClick={() => onNavigate(`/offer/${refBid}`)}
              className="text-[10px] font-semibold text-sky-600 hover:text-sky-700 flex items-center gap-0.5"
              data-testid={`alert-view-${alert.alert_id}`}
            >
              View <ChevronRight className="w-3 h-3" />
            </button>
          )}
          {alert.status === "active" && (
            <>
              <button
                onClick={() => onResolve(alert)}
                className="text-[10px] font-bold text-green-600 hover:text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-lg flex items-center gap-0.5"
                data-testid={`alert-resolve-${alert.alert_id}`}
              >
                <Check className="w-3 h-3" /> Resolve
              </button>
              <button
                onClick={() => onSnooze(alert.alert_id)}
                className="text-[10px] font-bold text-amber-600 hover:text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg flex items-center gap-0.5"
                data-testid={`alert-snooze-${alert.alert_id}`}
              >
                <Clock className="w-3 h-3" /> 7d
              </button>
              <button
                onClick={() => onDismiss(alert.alert_id)}
                className="text-[10px] font-semibold text-slate-400 hover:text-slate-600 flex items-center gap-0.5"
                data-testid={`alert-dismiss-${alert.alert_id}`}
              >
                <X className="w-3 h-3" /> Dismiss
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function AlertsPanel() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({ active: 0, dismissed: 0 });
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [selectedAlerts, setSelectedAlerts] = useState(new Set());

  const fetchAlerts = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/alerts?status=${status}`);
      setAlerts(res.data.alerts);
      setCounts(res.data.counts);
      setLastRefreshedAt(Date.now());
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts(filter);
  }, [filter, fetchAlerts]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/admin/alerts/scan`);
      toast.success(`Scan complete: ${res.data.alerts_created} new alert(s) generated`);
      fetchAlerts(filter);
    } catch {
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      await axios.put(`${API}/admin/alerts/${alertId}/dismiss`);
      toast.success("Alert dismissed");
      fetchAlerts(filter);
    } catch {
      toast.error("Failed to dismiss");
    }
  };

  const handleSnooze = async (alertId) => {
    try {
      await axios.post(`${API}/admin/alerts/${alertId}/snooze`, { days: 7 });
      toast.success("Alert snoozed for 7 days");
      fetchAlerts(filter);
    } catch {
      toast.error("Failed to snooze");
    }
  };

  const handleResolve = async (alert) => {
    try {
      await axios.put(`${API}/admin/alerts/${alert.alert_id}/dismiss`);
      toast.success(`Resolved: ${alert.title}`);
      fetchAlerts(filter);
    } catch {
      toast.error("Failed to resolve");
    }
  };

  const handleBatchAction = async (action) => {
    if (selectedAlerts.size === 0) return;
    try {
      const res = await axios.post(`${API}/admin/batch/resolve-alerts`, {
        alert_ids: [...selectedAlerts],
        action,
      });
      const actionId = res.data.action_id;
      toast.success(res.data.message, {
        action: actionId ? { label: "Undo", onClick: async () => {
          try { await axios.post(`${API}/admin/actions/${actionId}/undo`); toast.success("Undone"); fetchAlerts(filter); } catch { toast.error("Undo window expired"); }
        }} : undefined,
        duration: 6000,
      });
      setSelectedAlerts(new Set());
      fetchAlerts(filter);
    } catch {
      toast.error("Batch action failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="alerts-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={() => fetchAlerts(filter)} lastRefreshedAt={lastRefreshedAt} testId="alerts-refresh" /></div>
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          {[
            { key: "active", label: "Active", count: counts.active },
            { key: "snoozed", label: "Snoozed", count: counts.snoozed || 0 },
            { key: "dismissed", label: "Dismissed", count: counts.dismissed },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                filter === tab.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
              }`}
              data-testid={`alerts-tab-${tab.key}`}
            >
              {tab.label} {tab.count > 0 && <span className="ml-1 text-[10px]">({tab.count})</span>}
            </button>
          ))}
        </div>
        <button
          onClick={handleScan}
          disabled={scanning}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
          data-testid="alerts-scan-btn"
        >
          {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {scanning ? "Scanning..." : "Scan Now"}
        </button>
      </div>

      {/* Alert List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-8">
          <Bell className="w-8 h-8 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">
            {filter === "active" ? 'No active alerts. Hit "Scan Now" to check.' : `No ${filter} alerts.`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Batch bar */}
          {selectedAlerts.size > 0 && filter === "active" && (
            <div className="flex items-center gap-2 px-3 py-2 bg-sky-50 border border-sky-200 rounded-xl" data-testid="alerts-batch-bar">
              <span className="text-xs font-bold text-sky-700">{selectedAlerts.size} selected</span>
              <button onClick={() => handleBatchAction("dismissed")} className="px-2.5 py-1 rounded-lg bg-slate-600 text-white text-[10px] font-bold hover:bg-slate-700" data-testid="alerts-batch-resolve">Resolve All</button>
              <button onClick={() => handleBatchAction("snoozed")} className="px-2.5 py-1 rounded-lg bg-amber-500 text-white text-[10px] font-bold hover:bg-amber-600" data-testid="alerts-batch-snooze">Snooze All 7d</button>
              <button onClick={() => setSelectedAlerts(new Set())} className="text-[10px] text-slate-400 hover:text-slate-600 ml-auto">Clear</button>
            </div>
          )}
          {/* Select all */}
          {filter === "active" && alerts.length > 1 && (
            <label className="flex items-center gap-2 px-1 cursor-pointer">
              <input type="checkbox" checked={selectedAlerts.size === alerts.length} onChange={(e) => { e.target.checked ? setSelectedAlerts(new Set(alerts.map(a => a.alert_id))) : setSelectedAlerts(new Set()); }} className="w-3.5 h-3.5 rounded accent-sky-500" data-testid="alerts-select-all" />
              <span className="text-[10px] text-slate-400 font-medium">Select all ({alerts.length})</span>
            </label>
          )}
          {alerts.map((a) => (
            <div key={a.alert_id} className="flex items-start gap-2">
              {filter === "active" && (
                <input type="checkbox" checked={selectedAlerts.has(a.alert_id)} onChange={(e) => { const s = new Set(selectedAlerts); e.target.checked ? s.add(a.alert_id) : s.delete(a.alert_id); setSelectedAlerts(s); }} className="w-3.5 h-3.5 mt-3 rounded accent-sky-500 flex-shrink-0" data-testid={`alert-check-${a.alert_id}`} />
              )}
              <div className="flex-1 min-w-0">
                <AlertCard alert={a} onDismiss={handleDismiss} onSnooze={handleSnooze} onResolve={handleResolve} onNavigate={navigate} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
